import * as survey from "@aidkitorg/types/lib/survey";
import { CompileExpressionToSQL } from "@aidkitorg/types/lib/translation/expr_to_sql";
import { expandGenericTemplates } from "@aidkitorg/types/lib/translation/templates"
import React, { Suspense, useCallback, useContext, useEffect, useRef, useState } from "react";
import { Button, Icon, KeyboardShortcutsHelp, useIsMobile, useLocalizedStrings, useToast } from "@aidkitorg/component-library";
import { usePost } from "./API";
import InterfaceContext, { UserInfoContext } from "./Context";
import { DashboardComponent } from "./DistroDashboard";
import { getDistroBrowserIncompatibility } from "./Util";

function utf8_to_b64(str: string) {
  return window.btoa(unescape(encodeURIComponent(str)));
}

function b64_to_utf8(str: string) {
  return decodeURIComponent(escape(window.atob(str)));
}

function tryDecodingHash(hash: string) {
  try {
    return JSON.parse(b64_to_utf8(hash));
  } catch (e) {
    return null;
  }
}

export function encodeQueueForExplore(q: survey.Queue, sectionFilter?: survey.BooleanExpr): string {
  // create 'And' expression to include sectionFilters if needed
  let andFilter = {
    kind: 'And',
    clauses: [
      q.condition.kind === 'Click' ? q.condition.expr : q.condition,
      ...(sectionFilter ? [sectionFilter] : []), // only add sectionFilter if it exists
      {
        'kind': 'Value Empty',
        'value': {
          'kind': 'Field',
          'field': q.assigneeField
        }
      }
    ]};

  // put the andFilter into an applicant table view of the queue - including queue order (if exists)
  const exploreQuery = {
    query: {
      kind: 'Applicant Table',
      filter: q.condition.kind === 'Click' ? {
        kind: 'Click',
        expr: andFilter,
        orderBy: q.condition.orderBy
      } : andFilter,
      columns: [
        { kind: 'Field', field: 'legal_name'},
        ...(q.columns || []),
      ]
    }
  };
  return utf8_to_b64(JSON.stringify(exploreQuery));
}

const Distro = React.lazy(() => import("@aidkitorg/typesheets/lib/distroeditor"));
export function Explore() {
  const getScope = usePost('/program/get_fields_scope');
  const doMagic = usePost('/program/admin/magic');
  const runQuery = usePost('/dashboard/view');
  const context = useContext(InterfaceContext);
  const params = new URLSearchParams(window.location.search);
  const user = useContext(UserInfoContext);
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState<any>(null);
  const [showQuery, setShowQuery] = useState(false);
  const [dash, setDash] = useState<any>(null);
  const defaultQuery: survey.Explore = tryDecodingHash(window.location.hash.slice(1)) || {
    query: {
      kind: 'Applicant Table',
      columns: [],
      filter: {
        kind: 'Never'
      }
    }
  };
  const [query, setQuery] = useState(defaultQuery);
  // queryRef is kept in sync with query state and is used by the keyboard shortcut handler
  // to avoid stale closures. Always update both together.
  const queryRef = useRef(defaultQuery);
  const distroRef = useRef<React.ComponentRef<typeof Distro>>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const { toast } = useToast();
  const runTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isMobile = useIsMobile();
  const L = useLocalizedStrings();

  useEffect(() => {
    if (params.get('run') === 'yes') run();
  }, [])

  const run = useCallback(async () => {
    // Cancel any pending runs - this shouldn't happen since the button is disabled when running
    // but it can happen with the keyboard shortcut, so adding here to be safe.
    if (runTimeoutRef.current) {
      clearTimeout(runTimeoutRef.current);
      runTimeoutRef.current = null;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const dashboard: survey.Dashboard = {
      kind: 'Dashboard',
      version: 1,
      path: '',
      title: 'Explore',
      components: [
        queryRef.current.query
      ]
    };
    setDash(dashboard);
    setRunning(true);
    try {                   
      setResults( 
        await Promise.race([
          runQuery( { dashboard, lang: context.lang }, abortControllerRef.current.signal ) as any,
          new Promise((_, reject) => setTimeout(
            () => reject(new Error('Query timeout')), 
            30000 // Default API gateway timeout
          ))
        ])
      );
    } catch (e:any) {
      if (e.name !== 'AbortError') {
        toast({
          description: e.message,
          variant: 'error'
        })
      }
    } finally {
      setRunning(false);
    }
  }, []);

  const handleRunQueryKeyboardShortcut = useCallback(async () => {
    // Cancel any pending runs before starting a new one.
    if (runTimeoutRef.current) {
      clearTimeout(runTimeoutRef.current);
      runTimeoutRef.current = null;
    }
    // If the query is already running, instead cancel it.
    if (running) {
      await cancel();
      return;
    };
    setRunning(true);
    // Distro has a debounce timeout of 500ms, so we need to make sure that has triggered before
    // we run the query.
    runTimeoutRef.current = setTimeout(() => run(), 600);
  }, [cancel, run, running])

  // Keyboard shortcuts dialog
  const [showKeyboardHelp, setShowKeyboardHelp] = useState(false);
  const toggleKeyboardHelp = useCallback(() => {
    setShowKeyboardHelp(prev => !prev);
  }, []);
  const closeKeyboardHelp = useCallback(() => {
    setShowKeyboardHelp(false);
  }, []);

  async function cancel() {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setRunning(false);
  }

  (window as any).magic = async (data: any) => {
    return await doMagic(data);
  }

  useEffect(() => {
    (async () => {
      const scope = await getScope({});
      if (scope?.fieldScope) {
        distroRef.current?.initialize(query, user, undefined, undefined, {
          'fields': scope.fieldScope,
        }, `We are using a tool to generate queries and charts to explore data in a database. These queries and charts are defined using JSON.

            The database has the following fields: ${scope.fieldScope.join(', ')}

            As an example, this is a bar chart that shows the race of veterans: {"kind":"Bar Chart","title":"Gender of people who like the color blue","summary":{"kind":"Count"},"groups":[{"kind":"Field","field":"race"}],"filter":{"kind":"And","clauses":[{"kind":"Equals","field":"veteran","value":"yes"}]}}

            This is a table of all applicants with their name and birth date: {"kind":"Applicant Table","columns":[{"kind":"Field","field":"legal_name"},{"kind":"Field","field":"birth_date"}],"filter":{"kind":"Not","clause":{"kind":"Never"}}}

            Note: that to check if a field equals a value, the correct form is {"kind": "Equals", field: [FIELD], value: [VALUE]}`

        );
      }
    })()
  }, [user]);

  const incompatible = getDistroBrowserIncompatibility();
  if (incompatible) return incompatible;

  return <div className="bg-gray-100 h-full p-4">
    <h2>Query</h2>
    <div className="mt-4 bg-white shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
      <Suspense>
        <Distro 
          ref={distroRef} 
          value= {query}
          types='src/survey.ts'
          name='Explore'
          onChange={(v) => {
            window.location.hash = '#' + utf8_to_b64(JSON.stringify(v));
            const expanded = expandGenericTemplates(v);
            queryRef.current = expanded;
            setQuery(expanded);
          }}
        />
      </Suspense>
    </div>
    <div className="flex justify-between">
      <div className="mt-2 inline-flex">
        <Button
          onClick={run}
          type="button"
          loading={running}
          isDisabled={running}
          variant="primary"
          className='m-2'
        >
          Run
        </Button>
        {running && <Button
          onClick={cancel}
          type="button"
          variant="danger"
          className='m-2'
        >
          Cancel
        </Button>}
        {['Applicant Table', 'Bar Chart', 'Pie Chart', 'Hero Count'].includes(query.query.kind) && (
          <Button
            onClick={() => setShowQuery(!showQuery)}
            type="button"
            variant="secondary"
            className='m-2'
          >
            {showQuery ? 'Hide' : 'Show'} SQL
          </Button>
        )}
      </div>
      <div className='mt-2'>
        {!isMobile && (<Button
          variant="unstyled"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            toggleKeyboardHelp();
          }}
          icon={<Icon name="QuestionMarkCircleIcon" type="solid" />}
          aria-label={L.component_library.keyboard_shortcuts_help.show_keyboard_shortcuts}
          className={`p-2 bg-gray-300 hover:bg-gray-300 rounded-full`}
        />)}
        <KeyboardShortcutsHelp
          title="Keyboard Shortcuts"
          shortcuts={[
            { key: 'Enter', description: 'Run/cancel the query', modifier: 'meta', callback: handleRunQueryKeyboardShortcut },
            { key: '/', description: 'Comment/uncomment SQL', modifier: 'meta', /* handled by CodeEditor */ },
          ]}
          isOpen={showKeyboardHelp}
          onClose={closeKeyboardHelp}
        />
      </div>
    </div>
    <div className="mt-4">
      {showQuery && <pre>{(() => {
        const q = query.query;
        switch (q.kind) {
          case 'Applicant Table':
            return CompileExpressionToSQL({
              cond: q.filter.kind === 'Click' ? q.filter.expr : q.filter,
              columns: q.columns,
              orderBy: q.filter.kind === 'Click' ? q.filter.orderBy : undefined
            });
          case 'Bar Chart':
            return CompileExpressionToSQL({
              cond: q.filter ?? { kind: 'Not', clause: { kind: 'Never' } },
              columns: [q.summary],
              groups: [...q.groups, ...(q.bars?.values || [])],
              countEmpty: q.countEmpty,
              allowCommasInGroups: q.allowCommasInGroups
            });
          case 'Pie Chart':
            return CompileExpressionToSQL({
              cond: q.filter ?? { kind: 'Not', clause: { kind: 'Never' } },
              columns: [q.summary],
              groups: q.groups,
              countEmpty: q.countEmpty,
              allowCommasInGroups: q.allowCommasInGroups
            });
          case 'Hero Count':
            return CompileExpressionToSQL({
              cond: q.filter
            });
          default:
            return '';
        }
      })()}
      </pre>}
      {!!results &&
        (typeof results === 'string'
          ? <div>{results}</div>
          : (<>
            <h1>Results</h1>
            {(results.components || []).map((s: survey.DashboardComponent & { sql?: string; error?: string }, i: number) => {
              if (s.error && s.sql) {
                return <div key={i} className="mb-4">
                  <p className="text-red-600 font-medium">Error: {s.error}</p>
                  <details className="mt-2">
                    <summary className="cursor-pointer text-sm text-gray-600">SQL that was run</summary>
                    <pre className="mt-2 p-2 bg-gray-100 text-xs overflow-auto max-h-60">{s.sql}</pre>
                  </details>
                </div>;
              }
              return <DashboardComponent
                key={s.kind + i}
                section={s}
                updateFilterConfig={(path: string, idx: number) => {}}
                isExploreQuery={true}
                dashboard={ dash }
                filterConfig={{} as Record<string, number>}
                lang={context.lang}
              />
            })}
          </>))}
    </div>
  </div>
}
